/**
 * libraryDoctypeStore — durable per-library target_doctype mapping
 * in browser localStorage.
 *
 * Captures the writer's "what does THIS library feed?" answer
 * (CSR / PSUR / CER / IB) so the LibraryHub can narrow the per-doc
 * classification dropdown to the right vocabulary
 * (see ``libraryDoctypeVocabulary.ts``).
 *
 * Mirrors ``chatHistoryStore.ts`` discipline: schema-versioned
 * envelope, pure parse/encode helpers, fail-soft on every read path,
 * ``window.localStorage`` access guarded by ``storageAvailable()``.
 *
 * SRP: one module, one concern — persist the libraryId → TargetDoctype
 * map. No React, no DOM beyond ``window.localStorage``, no network.
 *
 * # DECISION: single workspace-wide map keyed by libraryId, NOT a
 * #   per-library file. Stored as one JSON envelope.
 * # WHY: the value per library is a single short enum string. The
 * #      addin reads the whole map on shell mount (handful of
 * #      libraries, KB-scale payload) and writes on edit. A
 * #      per-library file would multiply localStorage entries with
 * #      no benefit; one envelope keeps reads + writes O(1) and
 * #      makes the schema bump (when it lands) a single migration.
 * # DATE: 2026-04-27
 *
 * # DECISION: backend ignorance is intentional. The addin is the
 * #   sole writer of this data today.
 * # WHY: ``createLibrary`` API doesn't accept ``target_doctype``
 * #      yet. Capturing the writer's intent client-side now means:
 * #      (a) the LibraryHub can narrow dropdowns immediately,
 * #      (b) when the backend adds the field, the addin already
 * #          has the value to send (no UX change for the writer).
 * #      Frontend-only is the honest scope of this Phase 9 ship.
 * # DATE: 2026-04-27
 *
 * # DECISION: target-doctype change PRESERVES existing per-doc
 * #   classifications.
 * # WHY: writer-confirmed (Q4, 2026-04-27). Classifications are
 * #      writer-approved data; clearing them on a target switch
 * #      would silently destroy work. Cross-target source types
 * #      (Protocol, SAP, etc.) overlap heavily; the few mismatches
 * #      are easier to manually re-correct than to redo every doc.
 * # DATE: 2026-04-27
 *
 * # DECISION: Fail-soft on every read AND write path.
 * # WHY: target_doctype is advisory UX. Quota-exceeded, corrupt
 * #      JSON, private browsing — none should block the writer's
 * #      run or crash the pane. ``saveLibraryDoctype`` returns
 * #      ``false`` on failure; today nothing surfaces it
 * #      (best-effort, mirrors ``saveResumePointer`` /
 * #      ``saveHistory``).
 * # DATE: 2026-04-27
 */

import { type TargetDoctype, isTargetDoctype } from "../libraryDoctypeVocabulary";

// ---------------------------------------------------------------------------
// Constants — exported for test reach + caller introspection
// ---------------------------------------------------------------------------

const STORAGE_KEY = "asthra.variantC.libraryDoctype.v1";
const SCHEMA_VERSION = 1;

interface StorageEnvelope {
  schema: number;
  /** libraryId → TargetDoctype string. Untyped string here (vs.
   *  TargetDoctype) so legacy/foreign values survive parse without
   *  crashing; ``loadLibraryDoctypeMap`` filters at the boundary. */
  byLibrary: Record<string, string>;
}

/** Public read shape — every value is a validated TargetDoctype.
 *  ``ReadonlyMap`` so callers can't mutate the loaded snapshot. */
export type LibraryDoctypeMap = ReadonlyMap<string, TargetDoctype>;

// ---------------------------------------------------------------------------
// Pure helpers — testable without storage access
// ---------------------------------------------------------------------------

/** Pure parse — null on any malformed input, never throws.
 *  Filters out entries whose value isn't a valid ``TargetDoctype``
 *  so a stale/foreign payload can't poison the in-memory map. */
export function parseEnvelope(raw: string | null): LibraryDoctypeMap | null {
  if (!raw) return null;
  let decoded: unknown;
  try {
    decoded = JSON.parse(raw);
  } catch {
    return null;
  }
  if (
    typeof decoded !== "object" ||
    decoded === null ||
    (decoded as StorageEnvelope).schema !== SCHEMA_VERSION ||
    typeof (decoded as StorageEnvelope).byLibrary !== "object" ||
    (decoded as StorageEnvelope).byLibrary === null
  ) {
    return null;
  }
  const out = new Map<string, TargetDoctype>();
  for (const [libraryId, value] of Object.entries((decoded as StorageEnvelope).byLibrary)) {
    if (typeof libraryId !== "string" || libraryId.length === 0) continue;
    if (isTargetDoctype(value)) out.set(libraryId, value);
  }
  return out;
}

/** Pure encode — produces the on-disk envelope JSON. */
export function encodeEnvelope(map: LibraryDoctypeMap): string {
  const byLibrary: Record<string, string> = {};
  for (const [libraryId, target] of map) {
    byLibrary[libraryId] = target;
  }
  const envelope: StorageEnvelope = {
    schema: SCHEMA_VERSION,
    byLibrary,
  };
  return JSON.stringify(envelope);
}

// ---------------------------------------------------------------------------
// localStorage I/O — fail-soft wrappers
// ---------------------------------------------------------------------------

function storageAvailable(): boolean {
  try {
    return typeof window !== "undefined" && !!window.localStorage;
  } catch (err) {
    logDev("window.localStorage inaccessible", err);
    return false;
  }
}

function logDev(label: string, err: unknown): void {
  if (typeof console === "undefined") return;
  console.warn(`[libraryDoctypeStore] ${label}:`, err);
}

/**
 * Load the full library → target_doctype map. Returns an empty map
 * when storage is unavailable, the slot is empty, OR the payload
 * is corrupt — never throws.
 */
export function loadLibraryDoctypeMap(): LibraryDoctypeMap {
  if (!storageAvailable()) return new Map();
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch (err) {
    logDev("localStorage.getItem threw", err);
    return new Map();
  }
  return parseEnvelope(raw) ?? new Map();
}

/**
 * Read the target_doctype for one library. Convenience over
 * ``loadLibraryDoctypeMap`` for callers that only need one value.
 * Returns null for both "library not in map" and "storage
 * unavailable" — the caller treats both as "no target set yet".
 */
export function getLibraryDoctype(libraryId: string): TargetDoctype | null {
  if (!libraryId) return null;
  return loadLibraryDoctypeMap().get(libraryId) ?? null;
}

/**
 * Set the target_doctype for one library. Read-modify-write;
 * preserves entries for other libraries. Returns ``true`` on
 * success, ``false`` on any storage failure.
 */
export function setLibraryDoctype(libraryId: string, target: TargetDoctype): boolean {
  if (!libraryId) return false;
  if (!storageAvailable()) return false;
  const next = new Map(loadLibraryDoctypeMap());
  next.set(libraryId, target);
  try {
    window.localStorage.setItem(STORAGE_KEY, encodeEnvelope(next));
    return true;
  } catch (err) {
    logDev("localStorage.setItem failed (quota?)", err);
    return false;
  }
}

/**
 * Clear the target_doctype for one library (revert to "no target
 * set"). Idempotent; returns ``true`` when the slot is now empty
 * (whether it was set or not). ``false`` only on storage error.
 */
export function clearLibraryDoctype(libraryId: string): boolean {
  if (!libraryId) return true;
  if (!storageAvailable()) return true;
  const current = loadLibraryDoctypeMap();
  if (!current.has(libraryId)) return true;
  const next = new Map(current);
  next.delete(libraryId);
  try {
    window.localStorage.setItem(STORAGE_KEY, encodeEnvelope(next));
    return true;
  } catch (err) {
    logDev("localStorage.setItem failed (quota?)", err);
    return false;
  }
}
